<template>
  <div class="recruiter-pitch">
    <div v-if="errorBannerMessage" class="recruiter-pitch-error m-b-15">
      {{ errorBannerMessage }}
    </div>
    <BaseButton
      size="sm"
      class="add-candidate-button"
      @click="setShowCandidateSelect(!showCandidateSelect)"
    >
      <WdsStack align-center gap-sm data-test="add-candidate-button">
        <i class="zmdi zmdi-account-add" />
        {{ $t('Add a candidate') }}
      </WdsStack>
    </BaseButton>
    <CandidateSelect
      v-click-outside="onClickOutside"
      :show="showCandidateSelect"
      class="candidate-select"
      :candidates="filteredFreelancers"
      :show-create-button="true"
      :search-term="searchTerm"
      :loading="$apollo.queries.candidates.loading"
      :on-click-create="() => toggleShowRecruiterFreelancerModal(true)"
      @add-candidate="addCandidate"
      @input="updateSearchTerm"
    />
    <RecruiterFreelancerModal
      :show="showRecruiterFreelancerModal"
      :on-cancel="() => toggleShowRecruiterFreelancerModal(false)"
      :on-confirm="onConfirmAddFreelancer"
      :currency="currency"
      :title="$t('Create candidate')"
      :help-text="
        !managesWorkers
          ? $t('The candidate will not be contacted before they have been selected for a job.')
          : null
      "
      :manages-workers="managesWorkers"
      :error-text="$t('An error occurred while trying to invite candidate')"
      :save-button-text="$t('Add')"
      :save-button-text-while-working="$t('Adding')"
    />
    <WdsAlert v-if="formErrors['input.candidates']" error class="tw-mt-2">
      {{ formErrors['input.candidates'][0] }}
    </WdsAlert>
    <WdsStack v-show="$apollo.queries.pitchedCandidates.loading" justify-center>
      <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
      <button-spinner :size="sizeNames.LG"></button-spinner>
    </WdsStack>
    <WdsStack align-start justify-space-between gap-custom="0px 16px" wrap>
      <PitchFreelancerCard
        v-for="(selectedCandidate, idx) in selectedCandidates"
        :key="selectedCandidate.freelancer.globalId"
        v-model:candidate="selectedCandidates[idx]"
        :avatar-path="
          selectedCandidate.freelancer.hasPicture ? selectedCandidate.freelancer.picture : undefined
        "
        :global-id="selectedCandidate.freelancer.globalId"
        :initial-comment="selectedCandidate.comment"
        :name="selectedCandidate.freelancer.name"
        :email="selectedCandidate.freelancer.email || selectedCandidate.inviteEmail"
        :initials="selectedCandidate.freelancer.initials"
        :currency="currency"
        :errors="{
          rate: formErrors[`input.candidates.${idx}.rate`],
          rateType: formErrors[`input.candidates.${idx}.rateType`],
          comment: formErrors[`input.candidates.${idx}.brief`],
          ...getLinksErrors(formErrors, idx),
        }"
        :profile-url="selectedCandidate.freelancer.primaryProfile?.url"
        @remove-freelancer="removeCandidate(idx, selectedCandidate.freelancer.globalId)"
      />
    </WdsStack>
    <div class="tw-mt-4 tw-mb-2 f-500">
      {{ $t('Write a message with your submission') }}
    </div>
    <WdsInputArea
      v-model="pitchMessage"
      name="recruiter-pitch"
      :placeholder="
        $t(
          'Enter e.g. a bit about your candidates and their qualifications and why they would be a good fit',
        )
      "
    />
    <div class="recruiter-pitch-footer">
      <WdsStack justify-space-between align-center>
        <div class="terms-banner c-gray-700">
          <div class="info-icon-wrapper">
            <i class="fa fa-info" />
          </div>
          {{ $t('When you submit, you also accept the') }}
          <a href="/pages/terms" class="c-gray-700" target="_blank">
            {{ `${$t('Worksome terms and conditions')}.` }}
          </a>
        </div>
        <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
        <ButtonWithSpinner
          color="primary"
          size="sm"
          :status="buttonStatus"
          :disabled="submitButtonDisabled"
          data-test="submit-candidates"
          @click="sendPitch"
        >
          {{ isEdit ? $t('Save submission') : $t('Submit') }}
        </ButtonWithSpinner>
      </WdsStack>
    </div>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { WdsStack, WdsInputArea, WdsAlert } from '@worksome/wds-components'
import isEqual from 'lodash/isEqual'
import { deepClone } from '@/utils/tools'
import BaseButton from '@/components/BaseButton.vue'
import PitchFreelancerCard from '@/components/PitchFreelancerCard.vue'
import CandidateSelect from '@/components/CandidateSelect.vue'
import { FormValidation } from '@/utils/formValidation'
import enums, { SizeNames } from '@/utils/enum'
import RecruiterFreelancerModal from '@/components/Contacts/RecruiterFreelancer/RecruiterFreelancerModal.vue'
import { AvailableCandidates } from '@/components/Contacts/RecruiterFreelancer/oldGraphql/queries'
import { QuickCreateRecruiterFreelancer } from '@/components/Contacts/RecruiterFreelancer/oldGraphql/mutations'

export default {
  components: {
    BaseButton,
    WdsStack,
    WdsInputArea,
    WdsAlert,
    PitchFreelancerCard,
    CandidateSelect,
    RecruiterFreelancerModal,
  },
  props: {
    /**
     * Job id that recruiter is pitching candidates for
     */
    jobId: {
      type: Number,
      required: true,
    },
    /**
     * Supplier id / id of recruiter
     */
    supplierId: {
      type: String,
      required: true,
    },
    /**
     * Rate type of job posting (ex. Rate per hour or Rate per day)
     */
    rateType: {
      type: String,
      required: true,
    },
    /**
     * Rate type name
     */
    rateTypeName: {
      type: String,
      required: true,
    },
    /**
     * Currency of job posting
     * */
    currency: {
      type: String,
      required: true,
    },
    /**
     * Initial message recruiter sent with pitch. This is only relevant in an edit flow.
     * */
    initialMessage: {
      type: String,
      default: '',
    },
    /**
     * Redirect route on successful pitch.
     * */
    redirectRoute: {
      type: String,
      default: '',
    },
    /**
     * If the managed workers setup is being used.
     * */
    managesWorkers: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      errorBannerMessage: '',
      pitchMessage: this.initialMessage,
      selectedCandidates: [],
      formErrors: {},
      showCandidateSelect: false,
      searchTerm: '',
      candidates: [],
      buttonStatus: 'enabled',
      showRecruiterFreelancerModal: false,
      sizeNames: SizeNames,
      initialSelectedCandidates: [],
      candidatesUpdated: false,
      messageUpdated: false,
    }
  },
  computed: {
    // Only display candidates in the dropdown who (1) have not already been included
    // in the pitch and (2) match the current search term
    filteredFreelancers() {
      return this.candidates
        .filter(
          (recruiterFreelancer) =>
            this.freelancerIsAvailable(recruiterFreelancer) &&
            this.freelancerMatchesSearchTerm(recruiterFreelancer),
        )
        .sort(
          (recruiterFreelancerA, recruiterFreelancerB) =>
            new Date(recruiterFreelancerB.createdAt).getTime() -
            new Date(recruiterFreelancerA.createdAt).getTime(),
        )
    },
    submitButtonDisabled() {
      if (this.$apollo.queries.pitchedCandidates.loading) {
        return true
      }

      if (this.buttonStatus === 'working') {
        return true
      }

      if (this.isEdit) {
        return (
          this.selectedCandidates.length === 0 || !(this.candidatesUpdated || this.messageUpdated)
        )
      }

      return false
    },
    isEdit() {
      return this.initialSelectedCandidates.length > 0
    },
  },
  apollo: {
    candidates: {
      query: AvailableCandidates,
      variables() {
        return {
          rateType: this.rateType,
          currency: this.currency,
        }
      },
      update(data) {
        return data?.me?.activeAccount?.candidates?.data
      },
    },
    pitchedCandidates: {
      query: gql`
        query pitchedCandidates(
          $jobId: Int!
          $recruiterId: ID!
          $rateType: RateType!
          $currency: String!
        ) {
          me {
            activeAccount {
              globalId
              ... on Recruiter {
                pitchedOffers(yjobId: $jobId, status: [OPEN, ACCEPTED]) {
                  data {
                    globalId
                    localId
                    rate
                    ratetype
                    brief
                    links
                    availableFrom
                    files {
                      name
                      globalId
                      localId
                      createdAt
                    }
                    freelancer {
                      localId
                      globalId
                      name
                      picture
                      hasPicture
                      initials
                      primaryProfile {
                        url
                      }
                      candidates(filter: { recruiterId: $recruiterId }, first: 1) {
                        data {
                          rate(rateType: $rateType, currency: $currency)
                          inviteEmail
                          hourRate
                          dailyRate
                          monthlyRate
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `,
      variables() {
        return {
          jobId: this.jobId,
          recruiterId: this.supplierId,
          rateType: this.rateType,
          currency: this.currency,
        }
      },
      update(data) {
        data.me.activeAccount.pitchedOffers.data
          .map((offer) => ({
            offerId: offer.localId,
            globalId: offer.globalId,
            rate: offer.rate,
            comment: offer.brief,
            freelancer: {
              ...offer.freelancer,
              rate: offer.freelancer.candidates?.data[0]?.rate,
              email: offer.freelancer.candidates?.data[0]?.inviteEmail,
            },
            links: offer.links?.map((link, index) => ({ key: index, url: link })) ?? [],
            files: [...offer.files].sort(
              (file1, file2) => new Date(file2.createdAt) - new Date(file1.createdAt),
            ),
            availableFrom: offer.availableFrom,
            rateType: offer.ratetype.toUpperCase(),
            hourRate: offer.freelancer.candidates?.data[0]?.hourRate,
            dailyRate: offer.freelancer.candidates?.data[0]?.dailyRate,
            monthlyRate: offer.freelancer.candidates?.data[0]?.monthlyRate,
          }))
          .forEach((offer) => {
            this.selectedCandidates.push(offer)
            this.initialSelectedCandidates.push(offer)
          })
      },
    },
  },
  watch: {
    pitchMessage() {
      this.messageUpdated = this.pitchMessage.length > 0
    },
    selectedCandidates: {
      handler() {
        if (this.isEdit) {
          // If there are no candidates, we can't really proceed
          if (this.selectedCandidates.length === 0) {
            this.candidatesUpdated = false

            return
          }

          let hasChanged = false

          // Determine if the candidate list has changed
          this.initialSelectedCandidates.every((initialSelectedCandidate) => {
            const matchingCandidate = this.selectedCandidates.find(
              (candidate) =>
                candidate.freelancer.globalId === initialSelectedCandidate.freelancer.globalId,
            )

            let candidateChanged = false

            if (matchingCandidate) {
              candidateChanged = !isEqual(initialSelectedCandidate, matchingCandidate)
            } else {
              // Most likely a new candidate
              candidateChanged = true
            }

            if (hasChanged === false && candidateChanged) {
              hasChanged = true
            }

            return true
          })

          this.candidatesUpdated = hasChanged
        }
      },
      deep: true,
    },
  },
  methods: {
    toggleShowRecruiterFreelancerModal(value) {
      this.showRecruiterFreelancerModal = value
    },
    freelancerIsAvailable(recruiterFreelancer) {
      return !this.selectedCandidates.find(
        (selectedCandidate) =>
          selectedCandidate.freelancer.globalId === recruiterFreelancer.freelancer.globalId,
      )
    },
    freelancerMatchesSearchTerm(recruiterFreelancer) {
      return (
        recruiterFreelancer.inviteEmail.toLowerCase().includes(this.searchTerm) ||
        recruiterFreelancer.freelancer.name.toLowerCase().includes(this.searchTerm)
      )
    },
    addCandidate(candidate) {
      this.selectedCandidates = [
        ...this.selectedCandidates,
        {
          ...candidate,
          rateType: this.rateType,
          files: candidate?.files ?? [],
          links: candidate?.links.map((link, index) => ({ key: index, url: link })) ?? [],
        },
      ]
      this.showCandidateSelect = false
      this.searchTerm = ''
      this.formErrors['input.candidates'] = undefined
    },
    removeCandidate(candidateIndex, globalId) {
      const {
        [`input.candidates.${candidateIndex}.rate`]: rate,
        [`input.candidates.${candidateIndex}.brief`]: brief,
        ...remainingErrors
      } = this.formErrors

      // Clear form errors for removed candidates
      this.formErrors = remainingErrors

      this.selectedCandidates = this.selectedCandidates.filter(
        (selectedCandidate) => selectedCandidate.freelancer.globalId !== globalId,
      )
    },
    async sendPitch() {
      if (this.selectedCandidates.length === 0) {
        this.formErrors['input.candidates'] = [
          $t('Add at least one possible candidate for the job'),
        ]
      }

      if (this.formErrors['input.candidates']) {
        return
      }

      this.buttonStatus = 'working'

      const input = {
        supplierId: this.supplierId,
        message: this.pitchMessage,
        candidates: [...this.selectedCandidates].map((candidate) => ({
          offerId: candidate.offerId || null,
          freelancerId: candidate.freelancer.globalId,
          rate: candidate.rate,
          // Remove duplicate or empty links
          links: candidate.links?.length
            ? [...new Set(candidate.links.filter((link) => link.url).map((link) => link.url))]
            : null,
          files: candidate.files?.length ? candidate.files.map((file) => file.globalId) : undefined,
          rateType: candidate.rateType,
          brief: candidate.comment || null,
          availableFrom: candidate.availableFrom,
        })),
      }

      this.$apollo
        .mutate({
          mutation: gql`
            mutation ($yjobId: Int!, $input: PitchFreelancersInput!) {
              pitchFreelancers(yjobId: $yjobId, input: $input) {
                globalId
                localId
              }
            }
          `,
          variables: {
            yjobId: this.jobId,
            input,
          },
        })
        .then(({ data }) => {
          window.location.href = `${this.redirectRoute}/${data.pitchFreelancers.localId}`
        })
        .catch(({ graphQLErrors }) => {
          this.buttonStatus = 'enabled'

          if (FormValidation.containsFormValidations(graphQLErrors)) {
            this.formErrors = FormValidation.fromGraphqlErrors(graphQLErrors)
          } else {
            this.errorBannerMessage = $t('An error occurred while trying to submit candidates')

            this.$store.commit('sendNotification', {
              type: enums.notification.types.ERROR,
              msg: this.$t(`An error occurred while trying to submit candidates`),
              sender: this,
              severity: enums.notification.severity.CRITICAL,
            })
          }
        })
    },
    setShowCandidateSelect(showCandidateSelect) {
      this.showCandidateSelect = showCandidateSelect
    },
    updateSearchTerm(event) {
      this.searchTerm = event.target.value.toLowerCase()
    },
    onClickOutside() {
      if (!this.showRecruiterFreelancerModal) {
        this.setShowCandidateSelect(false)
        this.searchTerm = ''
      }
    },
    async onConfirmAddFreelancer(data) {
      const createRecruiterFreelancerMutation = {
        mutation: QuickCreateRecruiterFreelancer,
        variables: {
          firstName: data.firstName,
          middleName: data.middleName,
          lastName: data.lastName,
          email: data.email,
          jobTitle: data.jobTitle,
          currency: this.currency,
          rateType: this.rateType,
          hourlyRate: data.hourlyRate,
          dailyRate: data.dailyRate,
          monthlyRate: data.monthlyRate,
          tags: data.tags,
          files: data.files,
          links: data.links,
        },
        update: (cache, { data: { createCandidate } }) => {
          const newData = deepClone(
            cache.readQuery({
              query: AvailableCandidates,
              variables: {
                rateType: this.rateType,
                currency: this.currency,
              },
            }),
          )
          newData.me.activeAccount.candidates.data.push(createCandidate)

          cache.writeQuery({
            query: AvailableCandidates,
            variables: {
              rateType: this.rateType,
              currency: this.currency,
            },
            data: newData,
          })
        },
      }

      return this.$apollo
        .mutate(createRecruiterFreelancerMutation)
        .then((result) => {
          this.addCandidate(result.data.createCandidate)
          return result
        })
        .catch((error) => {
          throw error
        })
    },
    getLinksErrors(formErrors, candidateIndex) {
      return Object.keys(formErrors)
        .filter((key) => key.startsWith(`input.candidates.${candidateIndex}.links.`))
        .reduce((accumulator, current) => {
          const newLinkKey = current.replace(`input.candidates.${candidateIndex}.`, '')
          accumulator[newLinkKey] = formErrors[current]
          return accumulator
        }, {})
    },
  },
}
</script>

<style scoped lang="scss">
@import 'resources/sass/frontend/_color-variables';

.add-candidate-button {
  margin-bottom: 8px;
}

.recruiter-pitch {
  margin-top: 28px;
}

.recruiter-pitch-footer {
  margin-top: 28px;

  .terms-banner {
    border-radius: 4px;
    padding: 12px;
    background-color: #f2f2f2;
    font-size: 12px;
    line-height: 16px;

    a {
      font-weight: 400;
      text-decoration: underline;
    }
  }
}

.info-icon-wrapper {
  border: 1px solid $gray-700;
  display: inline-flex;
  border-radius: 100%;
  width: 16px;
  height: 16px;
  justify-content: center;
  align-items: center;
  margin-right: 12px;
}

.recruiter-pitch-error {
  background-color: $red-50;
  border-radius: 4px;
  padding: 12px 14px;
}
</style>
